/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\VoicemailBroadcastSettingsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { Phone, Settings } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { MultiSelectButton } from '../../../components/ui/MultiSelectButton';
import { MultiSelectModal } from '../../../components/ui/MultiSelectModal';

interface NotificationSettings {
  method: 'none' | 'email' | 'api' | 'event';
  target: string;
}

interface FormSettings {
  announcement_path: string;
  distribution_mode: 'parallel' | 'sequential' | 'active_only';
  notification: NotificationSettings;
  max_record_time: number;
  max_retry: number;
  record_format: 'wav' | 'mp3' | 'ogg';
  delete_after_broadcast: boolean;
  announce_group_name: boolean;
  playback_volume: number;
  allowed_origins: string[];
}

const DEFAULT_MAX_RECORD_TIME = 300;
const DEFAULT_MAX_RETRY = 3;

interface VoicemailBroadcastSettingsSectionProps {
  settings: FormSettings;
  members: string[];
  errors: Record<string, string>;
  availableExtensions: Array<{ id: string; name: string; description: string; slug: string }>;
  showMembersModal: boolean;
  setShowMembersModal: (open: boolean) => void;
  onSettingsChange: <K extends keyof FormSettings>(key: K, value: FormSettings[K]) => void;
  onNotificationChange: <K extends keyof NotificationSettings>(key: K, value: NotificationSettings[K]) => void;
  onMembersChange: (members: string[]) => void;
  t: (key: string, fallback?: string) => string;
}

export function VoicemailBroadcastSettingsSection({
  settings, members, errors, availableExtensions,
  showMembersModal, setShowMembersModal,
  onSettingsChange, onNotificationChange, onMembersChange, t,
}: VoicemailBroadcastSettingsSectionProps) {
  return (
    <>
      <FormBox title={t('voicemailsBroadcastGroups:sections.settings')} icon={<Settings className="h-5 w-5" />} className="mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">

          {/* Announcement Path */}
          <Label htmlFor="announcement_path">
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.announcementPath.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.announcementPath.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <RecordingSelect value={settings.announcement_path}
              onChange={(v) => onSettingsChange('announcement_path', v || '')}
              placeholder={t('voicemailsBroadcastGroups:fields.announcementPath.placeholder')}
              returnType="id" showPlayButton />
          </div>

          {/* Distribution Mode */}
          <Label htmlFor="distribution_mode">
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.distributionMode.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.distributionMode.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={settings.distribution_mode} onValueChange={(v) => onSettingsChange('distribution_mode', v as FormSettings['distribution_mode'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parallel">{t('voicemailsBroadcastGroups:fields.distributionMode.options.parallel')}</SelectItem>
                <SelectItem value="sequential">{t('voicemailsBroadcastGroups:fields.distributionMode.options.sequential')}</SelectItem>
                <SelectItem value="active_only">{t('voicemailsBroadcastGroups:fields.distributionMode.options.active_only')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notification Method */}
          <Label htmlFor="notification_method">
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.notification.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.notification.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={settings.notification.method} onValueChange={(v) => onNotificationChange('method', v as NotificationSettings['method'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('voicemailsBroadcastGroups:fields.notification.options.none')}</SelectItem>
                <SelectItem value="email">{t('voicemailsBroadcastGroups:fields.notification.options.email')}</SelectItem>
                <SelectItem value="api">{t('voicemailsBroadcastGroups:fields.notification.options.api')}</SelectItem>
                <SelectItem value="event">{t('voicemailsBroadcastGroups:fields.notification.options.event')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notification Target (conditional) */}
          {settings.notification.method !== 'none' && (
            <>
              <Label htmlFor="notification_target">
                <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.notification.target.tooltip')}>
                  <span>
                    {t('voicemailsBroadcastGroups:fields.notification.target.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div className="col-span-3">
                <Input id="notification_target" value={settings.notification.target}
                  onChange={(e) => onNotificationChange('target', e.target.value)}
                  placeholder={t(`voicemailsBroadcastGroups:fields.notification.target.placeholder.${settings.notification.method}`)}
                  className={errors.notification_target ? 'border-red-500' : ''} />
                {errors.notification_target && <p className="text-sm text-red-500 mt-1">{errors.notification_target}</p>}
              </div>
            </>
          )}

          {/* Max Record Time */}
          <Label htmlFor="max_record_time">
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.maxRecordTime.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.maxRecordTime.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="max_record_time" type="number" min={10} max={3600} value={settings.max_record_time}
              onChange={(e) => onSettingsChange('max_record_time', Number(e.target.value) || DEFAULT_MAX_RECORD_TIME)}
              className={errors.max_record_time ? 'border-red-500' : ''} />
            {errors.max_record_time && <p className="text-sm text-red-500 mt-1">{errors.max_record_time}</p>}
          </div>

          {/* Max Retry */}
          <Label htmlFor="max_retry">
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.maxRetry.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.maxRetry.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="max_retry" type="number" min={0} max={10} value={settings.max_retry}
              onChange={(e) => onSettingsChange('max_retry', Number(e.target.value) || DEFAULT_MAX_RETRY)}
              className={errors.max_retry ? 'border-red-500' : ''} />
            {errors.max_retry && <p className="text-sm text-red-500 mt-1">{errors.max_retry}</p>}
          </div>

          {/* Record Format */}
          <Label htmlFor="record_format">
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.recordFormat.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.recordFormat.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={settings.record_format} onValueChange={(v) => onSettingsChange('record_format', v as FormSettings['record_format'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="wav">{t('voicemailsBroadcastGroups:fields.recordFormat.options.wav')}</SelectItem>
                <SelectItem value="mp3">{t('voicemailsBroadcastGroups:fields.recordFormat.options.mp3')}</SelectItem>
                <SelectItem value="ogg">{t('voicemailsBroadcastGroups:fields.recordFormat.options.ogg')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Delete After Broadcast */}
          <Label>
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.deleteAfterBroadcast.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.deleteAfterBroadcast.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle value={settings.delete_after_broadcast} onChange={(v) => onSettingsChange('delete_after_broadcast', v)} />
          </div>

          {/* Announce Group Name */}
          <Label>
            <SimpleTooltip content={t('voicemailsBroadcastGroups:fields.announceGroupName.tooltip')}>
              {t('voicemailsBroadcastGroups:fields.announceGroupName.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle value={settings.announce_group_name} onChange={(v) => onSettingsChange('announce_group_name', v)} />
          </div>

          {/* Playback Volume */}
          <Label htmlFor="playback_volume">
            <SimpleTooltip content={`${t('voicemailsBroadcastGroups:fields.playbackVolume.tooltip')} (${settings.playback_volume})`}>
              {t('voicemailsBroadcastGroups:fields.playbackVolume.label')}: {settings.playback_volume}
            </SimpleTooltip>
          </Label>
          <div className="col-span-3">
            <input id="playback_volume" type="range" min={1} max={10} value={settings.playback_volume}
              onChange={(e) => onSettingsChange('playback_volume', Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1</span><span>5</span><span>10</span>
            </div>
          </div>

          {/* Members Multi-Select */}
          <div className="col-span-4">
            <MultiSelectButton
              items={availableExtensions}
              selectedIds={members}
              onClick={() => setShowMembersModal(true)}
              label={t('voicemailsBroadcastGroups:fields.allowedOrigins.label')}
              placeholder={t('voicemailsBroadcastGroups:fields.allowedOrigins.placeholder')}
              icon={Phone}
              maxPreviewItems={3}
            />
          </div>
        </div>
      </FormBox>

      {/* Members Multi-Select Modal */}
      <MultiSelectModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        title={t('voicemailsBroadcastGroups:fields.allowedOrigins.modalTitle')}
        description={t('voicemailsBroadcastGroups:fields.allowedOrigins.modalDescription')}
        items={availableExtensions}
        selectedIds={members}
        onSelectionChange={onMembersChange}
        searchPlaceholder={t('voicemailsBroadcastGroups:fields.allowedOrigins.searchPlaceholder')}
        icon={Phone}
      />
    </>
  );
}
